import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { STOP_POINTS } from '@continuum/show-core';
import type { MeetingResult, Reveal, StopReason } from '@continuum/show-core';
import { BottomSheet, useSheet } from '../ui/BottomSheet.js';
import { GhostButton, Hero, PostLine, PrimaryButton, mono, sans } from '../ui/atoms.js';
import { Screen } from '../ui/Shell.js';
import { STOP_COPY } from '../ui/stopText.js';

/** A card's one line to read, as big as a title. */
const HEADLINE = { fontWeight: 800, lineHeight: 1.02, letterSpacing: 1.5 } as const;
import { ROUND_COPY } from '../ui/roundText.js';
import { GAME_COPY } from '../ui/gameText.js';
import { PrivatePill } from './Game.js';
import type { Lang } from '../ui/text.js';

/**
 * An era's round on the phone (Marek, 2026-09-28): the games open with every phone buzzing and a
 * full-screen PLAY NOW that opens the guest's own game (a saboteur's is their sabotage, games/Hack.tsx);
 * when the last game is done the stop goes one step at a time on every phone, each on the captain's
 * clicker (Marek, 2026-10-06; 2026-10-09): SHIP CHARGED, the alarms, the MEETING, the vote, the VERDICT,
 * the REVEAL of the stop's saboteurs with the points, and before the jump the next draw. One thing to
 * read, big, and one big thing to press.
 */

/** How long SHIP CHARGED holds the phone, as it holds the TV. */
export const CHARGED_CARD_MS = 6_000;

/** Dark words on a light colour, white on a dark one. */
function inkOn(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const luma = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
  return luma > 0.45 ? '#05070a' : '#ffffff';
}

function SheetButton({ children, bg, fg, onPress }: { children: ReactNode; bg: string; fg: string; onPress?: () => void }) {
  const { close } = useSheet();
  return (
    <PrimaryButton
      big
      bg={bg}
      fg={fg}
      onClick={() => {
        onPress?.();
        close();
      }}
    >
      {children}
    </PrimaryButton>
  );
}

function SheetLater({ label, color }: { label: string; color: string }) {
  const { close } = useSheet();
  return (
    <GhostButton color={color} border={`${color}66`} onClick={close}>
      {label}
    </GhostButton>
  );
}

/** The call to play: the whole phone in the era's colour, a buzz, and the one button that opens the game. */
export function PlayNowSheet({
  lang,
  year,
  accent,
  games,
  onPlay,
  onDone,
}: {
  lang: Lang;
  year: string;
  accent: string;
  /** The guest's games this era, own first, in their language (show-core `gamesOf`). */
  games: readonly string[];
  onPlay: () => void;
  onDone: () => void;
}) {
  const r = ROUND_COPY[lang];
  useEffect(() => {
    // Every phone at once, where the phone allows it (iPhones do not).
    navigator.vibrate?.([160, 90, 160]);
  }, []);
  const ink = inkOn(accent);
  return (
    <BottomSheet
      id="play-now"
      size="full"
      critical
      zIndex={62}
      label={r.playTitle}
      onDismiss={onDone}
      tone={{ bg: accent, fg: ink, border: accent, grip: ink }}
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SheetButton bg={ink} fg={accent} onPress={onPlay}>
            {r.playGo}
          </SheetButton>
          <SheetLater label={r.later} color={ink} />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', paddingTop: 12 }}>
        <div style={{ fontFamily: mono, fontSize: 14, fontWeight: 700, letterSpacing: 2 }}>{r.playEyebrow(year)}</div>
        <Hero color={ink} style={{ ...HEADLINE, marginTop: 18, fontSize: 64 }}>
          {r.playTitle}
        </Hero>
        <div style={{ marginTop: 16, fontFamily: sans, fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>{r.playGames(games)}</div>
        <div style={{ marginTop: 10, fontFamily: sans, fontSize: 19, fontWeight: 600, lineHeight: 1.3, opacity: 0.9 }}>{r.playLine}</div>
      </div>
    </BottomSheet>
  );
}

/** SHIP CHARGED — ALL CREW DONE: a few seconds, or OK. */
export function ChargedSheet({ lang, accent, onDone }: { lang: Lang; accent: string; onDone: () => void }) {
  const r = ROUND_COPY[lang];
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    navigator.vibrate?.(80);
    const id = setTimeout(() => done.current(), CHARGED_CARD_MS);
    return () => clearTimeout(id);
  }, []);
  const ink = inkOn(accent);
  return (
    <BottomSheet
      id="ship-charged"
      size="full"
      zIndex={62}
      label={r.chargedTitle}
      onDismiss={onDone}
      tone={{ bg: accent, fg: ink, border: accent, grip: ink }}
      footer={
        <SheetButton bg={ink} fg={accent}>
          {r.ok}
        </SheetButton>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 24 }}>
        <Hero color={ink} style={{ ...HEADLINE, fontSize: 56 }}>
          {r.chargedTitle}
        </Hero>
        <div style={{ marginTop: 14, fontFamily: sans, fontSize: 30, fontWeight: 800, letterSpacing: 2 }}>{r.chargedLine}</div>
        <div style={{ marginTop: 14, fontFamily: sans, fontSize: 20, fontWeight: 600, opacity: 0.9 }}>{r.chargedSub}</div>
      </div>
    </BottomSheet>
  );
}

const CREW = '#4fd8c2';
const AGENT = '#ff5f86';
const WARN = '#ffb547';
/** The role step's own colour: the same for everyone, never a saboteur's red. */
const ROLE = '#a08cff';

/**
 * Each era's role step (Marek, 2026-10-09: "a clear moment where at the beginning of each era they have
 * to hold to reveal if they are saboteurs"): every phone at once, the same screen for everyone; the role
 * itself only while held, on the role card (`RoleSheet`). No red line on a saboteur's phone any more.
 */
export function RoleStep({ lang, seen, onOpen }: { lang: Lang; seen: boolean; onOpen: () => void }) {
  const r = ROUND_COPY[lang];
  return (
    <Screen
      style={{ background: '#0c0a1a', color: '#eeeaff' }}
      footer={
        <PrimaryButton big bg={ROLE} fg="#0c0a1a" onClick={onOpen}>
          {seen ? r.roleStepAgain : r.roleStepOpen}
        </PrimaryButton>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
        <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 3, color: ROLE }}>{r.roleStepTitle}</div>
        <Hero color="#f3f0ff" style={{ ...HEADLINE, fontSize: 40 }}>
          {r.holdToSee}
        </Hero>
        <div style={{ fontFamily: sans, fontSize: 19, fontWeight: 600, lineHeight: 1.35, color: '#d9d2ff' }}>{seen ? r.roleStepSeen : r.roleStepLine}</div>
      </div>
    </Screen>
  );
}

/** The stop's meeting, before its vote: the table talks it over; the vote is the captain's next NEXT. */
export function TalkSheet({ lang, onDone }: { lang: Lang; step: 'meeting'; onDone: () => void }) {
  const r = ROUND_COPY[lang];
  useEffect(() => {
    navigator.vibrate?.(60);
  }, []);
  return (
    <BottomSheet
      id="stop-meeting"
      size="full"
      zIndex={62}
      label={r.talkTitle}
      onDismiss={onDone}
      tone={{ bg: '#1a1306', fg: '#f3ead6', border: WARN, grip: WARN }}
      footer={
        <SheetButton bg={WARN} fg="#1a1306">
          {r.ok}
        </SheetButton>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 24 }}>
        <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 3, color: WARN }}>{r.talkEyebrow}</div>
        <Hero color="#f3ead6" style={{ ...HEADLINE, fontSize: 44 }}>
          {r.talkTitle}
        </Hero>
        <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 600, lineHeight: 1.35 }}>{r.talkLine}</div>
      </div>
    </BottomSheet>
  );
}

function DrawAnswer({ title, accent, now, nowLabel, onPick }: { title: string; accent: string; now: boolean; nowLabel: string; onPick: () => void }) {
  const { close } = useSheet();
  return (
    <button
      className="cc-answer"
      onClick={() => {
        onPick();
        close();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        textAlign: 'left',
        borderRadius: 12,
        border: `1px solid ${now ? accent : `${accent}4d`}`,
        background: now ? `${accent}1f` : `${accent}08`,
        color: '#dfe9f2',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 2.5, color: accent }}>{title}</span>
      {now && <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.5, color: accent }}>{nowLabel}</span>}
    </button>
  );
}

/**
 * Before each jump, on every phone, privately (Marek, 2026-10-01): in the next stop's saboteur draw, or
 * not. The answer the guest has now is marked; a tap sends the new one — the same secret check-in set,
 * never on the wire — and a swipe away keeps it. The room draws at the jump, as it always has.
 */
export function DrawSheet({
  lang,
  willing,
  onChoose,
  onDone,
}: {
  lang: Lang;
  /** Their answer now (check-in's, or the last stop's). */
  willing: boolean | null;
  onChoose: (willing: boolean) => void;
  onDone: () => void;
}) {
  const r = ROUND_COPY[lang];
  return (
    <BottomSheet
      id="next-draw"
      size="full"
      zIndex={62}
      label={r.drawTitle}
      onDismiss={onDone}
      tone={{ bg: '#070a0e', fg: '#dfe9f2', border: 'rgba(255,255,255,.14)', grip: '#8fa4b5' }}
      footer={<div style={{ fontFamily: mono, fontSize: 11, color: '#6f808b', textAlign: 'center' }}>{r.drawNote}</div>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 12 }}>
        <PrivatePill text={GAME_COPY[lang].private} />
        <div style={{ marginTop: 18, fontFamily: mono, fontSize: 13, fontWeight: 700, letterSpacing: 2, color: '#9db3c2' }}>{r.drawEyebrow}</div>
        <Hero color="#dfe9f2" style={{ ...HEADLINE, marginTop: 10, fontSize: 40 }}>
          {r.drawTitle}
        </Hero>
        <div style={{ marginTop: 12, fontFamily: sans, fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{r.drawLine}</div>
        <div className="cc-answers">
          <DrawAnswer title={r.drawYes} accent={AGENT} now={willing === true} nowLabel={r.drawNow} onPick={() => onChoose(true)} />
          <DrawAnswer title={r.drawNo} accent={CREW} now={willing === false} nowLabel={r.drawNow} onPick={() => onChoose(false)} />
        </div>
      </div>
    </BottomSheet>
  );
}

/** Whether this phone is asked about the next stop's draw now: once its stop has run to the end, at stops 1–4. */
export function drawAsked(chapter: number, phase: string, stopDone: boolean): boolean {
  return chapter >= 1 && chapter <= 4 && phase === 'fieldwork' && stopDone;
}

// ------------------------------------------------------------------ the verdict

/** The votes, most first, as name → count. No skip any more (Marek, 2026-10-10): a stray one is never shown. */
export function tallyRows(tally: Record<string, number>, nameOf: (seat: number) => string): { key: string; name: string; votes: number }[] {
  return Object.entries(tally)
    .filter(([k, n]) => /^\d+$/.test(k) && n > 0)
    .map(([k, n]) => ({ key: k, name: nameOf(Number(k)), votes: n }))
    .sort((a, b) => b.votes - a.votes || a.name.localeCompare(b.name));
}

/** Who the stop's vote sent out, and whether they were a saboteur; or nobody. With the count. */
export function VerdictSheet({
  lang,
  result,
  seat,
  nameOf,
  onDone,
}: {
  lang: Lang;
  result: MeetingResult;
  /** This phone's seat. */
  seat: number;
  nameOf: (seat: number) => string;
  onDone: () => void;
}) {
  const t = STOP_COPY[lang];
  const out = result.sentToBrig;
  const accent = out === null ? WARN : result.caughtAgent ? CREW : AGENT;
  const rows = tallyRows(result.tally, nameOf);
  const most = Math.max(1, ...rows.map((r) => r.votes));
  useEffect(() => {
    navigator.vibrate?.(60);
  }, []);
  return (
    <BottomSheet
      id="verdict"
      size="full"
      zIndex={62}
      label={t.verdictEyebrow}
      onDismiss={onDone}
      tone={{ bg: '#0b0a06', fg: '#f3ead6', border: `${accent}88`, grip: accent }}
      footer={
        <SheetButton bg={accent} fg="#0b0a06">
          {t.ok}
        </SheetButton>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 8 }}>
        <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, letterSpacing: 2, color: accent }}>{t.verdictEyebrow}</div>
        <Hero color="#f3ead6" style={{ ...HEADLINE, marginTop: 12, fontSize: 'clamp(32px, 6.4vh, 44px)' }}>
          {out === null ? t.verdictNobody : t.verdictOut(nameOf(out))}
        </Hero>
        <div style={{ marginTop: 8, fontFamily: sans, fontSize: out === null ? 19 : 26, fontWeight: out === null ? 600 : 800, letterSpacing: out === null ? 0 : 1.5, color: accent }}>
          {out === null ? t.verdictNobodyWhy : result.caughtAgent ? t.verdictAgent : t.verdictNotAgent}
        </div>
        {out === seat && <div style={{ marginTop: 8, fontFamily: sans, fontSize: 18, fontWeight: 600, color: '#ffd1da' }}>{t.verdictYou}</div>}
        {rows.length > 0 && (
          <div style={{ marginTop: 'clamp(14px, 3vh, 24px)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((row) => (
              <div key={row.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 40%) 1fr auto', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: sans, fontSize: 18, fontWeight: 700, letterSpacing: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: row.name === null ? '#b9a678' : '#f3ead6' }}>
                  {row.name}
                </span>
                <span style={{ height: 10, borderRadius: 5, background: 'rgba(255,226,176,.1)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${(100 * row.votes) / most}%`, background: row.key === String(out) ? accent : 'rgba(255,226,176,.45)' }} />
                </span>
                <span style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: '#f3ead6' }}>{row.votes}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ------------------------------------------------------------------ the reveal

export type RevealReason = { kind: 'rightVote' | 'caught' | 'wronglyOut' | 'escaped' | 'damage'; points: number; count: number };

/**
 * Why the reveal gave this guest what it gave (`STOP_POINTS`), for a room that does not say (`Reveal.why`),
 * worked out from what the phone knows:
 * who the saboteurs were, who was voted out, and its own vote (kept on the phone, as ballots never
 * leave the room). A saboteur's points beyond being caught or getting away are the damage their alarm
 * did. Null when the parts do not add up to the room's total, so the phone shows the total alone.
 */
export function revealReasons(o: {
  seat: number;
  total: number;
  saboteurs: readonly number[];
  out: number | null;
  /** This phone's vote in the stop's meeting: a seat number, 'skip', or null when it does not know. */
  ballot: string | null;
}): RevealReason[] | null {
  const agent = o.saboteurs.includes(o.seat);
  const reasons: RevealReason[] = [];
  if (o.out === o.seat) reasons.push(agent ? { kind: 'caught', points: STOP_POINTS.CAUGHT, count: 1 } : { kind: 'wronglyOut', points: STOP_POINTS.WRONGLY_OUT, count: 1 });
  else if (agent) reasons.push({ kind: 'escaped', points: STOP_POINTS.ESCAPED, count: 1 });
  const picked = o.ballot !== null && /^\d+$/.test(o.ballot) ? Number(o.ballot) : null;
  if (picked !== null && picked !== o.seat && o.saboteurs.includes(picked)) reasons.push({ kind: 'rightVote', points: STOP_POINTS.RIGHT_VOTE, count: 1 });
  let rest = o.total - reasons.reduce((sum, r) => sum + r.points, 0);
  // A vote the phone has forgotten (a reload, another phone), when that is all the rest can be.
  if (!agent && o.ballot === null && rest === STOP_POINTS.RIGHT_VOTE) {
    reasons.push({ kind: 'rightVote', points: STOP_POINTS.RIGHT_VOTE, count: 1 });
    rest = 0;
  }
  if (agent && rest > 0 && rest % STOP_POINTS.DAMAGE === 0) {
    const count = rest / STOP_POINTS.DAMAGE;
    reasons.push({ kind: 'damage', points: rest, count });
    rest = 0;
  }
  return rest === 0 ? reasons : null;
}

const REASON: Record<StopReason, RevealReason['kind']> = {
  RIGHT_VOTE: 'rightVote',
  CAUGHT: 'caught',
  WRONGLY_OUT: 'wronglyOut',
  ESCAPED: 'escaped',
  DAMAGE: 'damage',
};

/** The room's own reasons for a guest's points (`Reveal.why`), one line a kind, the damage counted. */
export function reasonsFromRoom(why: readonly StopReason[]): RevealReason[] {
  const lines: RevealReason[] = [];
  for (const reason of why) {
    const kind = REASON[reason];
    const points = STOP_POINTS[reason];
    const had = lines.find((line) => line.kind === kind);
    if (had) {
      had.count += 1;
      had.points += points;
    } else lines.push({ kind, points, count: 1 });
  }
  return lines;
}

const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0');

/** The stop's saboteurs made public, caught or not, and what the end of the stop gave this guest. */
export function RevealSheet({
  lang,
  reveal,
  out,
  seat,
  ballot,
  nameOf,
  postOf,
  onDone,
}: {
  lang: Lang;
  reveal: Reveal;
  /** Who the stop's vote sent out, or null. */
  out: number | null;
  seat: number;
  /** This phone's vote in the stop's meeting, as it remembers it. */
  ballot: string | null;
  nameOf: (seat: number) => string;
  postOf: (seat: number) => string;
  onDone: () => void;
}) {
  const t = STOP_COPY[lang];
  const total = reveal.points[String(seat)] ?? 0;
  // The room says why (`Reveal.why`); a room from before only gives the total, and the phone works it out.
  const reasons = reveal.why ? reasonsFromRoom(reveal.why[String(seat)] ?? []) : revealReasons({ seat, total, saboteurs: reveal.seats, out, ballot });
  const label = (r: RevealReason) =>
    r.kind === 'rightVote' ? t.rightVote : r.kind === 'caught' ? t.caught : r.kind === 'wronglyOut' ? t.wronglyOut : r.kind === 'escaped' ? t.escaped : t.damage(r.count);
  const lines = reasons ? reasons.map((r) => `${signed(r.points)} · ${label(r)}`) : total !== 0 ? [signed(total)] : [];
  useEffect(() => {
    navigator.vibrate?.([80, 60, 80]);
  }, []);
  return (
    <BottomSheet
      id="reveal"
      size="full"
      zIndex={62}
      label={t.revealEyebrow}
      onDismiss={onDone}
      tone={{ bg: '#120509', fg: '#ffe6ec', border: `${AGENT}88`, grip: AGENT }}
      footer={
        <SheetButton bg={AGENT} fg="#2a0610">
          {t.ok}
        </SheetButton>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 8 }}>
        <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, letterSpacing: 2, color: AGENT }}>{t.revealEyebrow}</div>
        <Hero color="#ffe6ec" style={{ ...HEADLINE, marginTop: 12, fontSize: 'clamp(28px, 5.4vh, 38px)' }}>
          {reveal.seats.length > 0 ? t.revealTitle(reveal.seats.length) : t.revealNone}
        </Hero>
        {reveal.seats.length === 0 ? (
          <div style={{ marginTop: 10, fontFamily: sans, fontSize: 19, fontWeight: 600, lineHeight: 1.3, color: '#ffc2cf' }}>{t.revealNoneLine}</div>
        ) : (
          <div style={{ marginTop: 'clamp(12px, 2.4vh, 20px)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reveal.seats.map((s) => (
              <div
                key={s}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 14px',
                  borderRadius: 14,
                  border: `1px solid ${AGENT}`,
                  background: 'rgba(255,95,134,.12)',
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: sans, fontSize: 26, fontWeight: 800, letterSpacing: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {nameOf(s)}
                    {s === seat ? <span style={{ marginLeft: 8, fontFamily: mono, fontSize: 12, letterSpacing: 1.5, color: '#ffc2cf' }}>{t.revealYou}</span> : null}
                  </span>
                  <PostLine color="#ffc2cf">{postOf(s)}</PostLine>
                </span>
                <span style={{ flex: 'none', fontFamily: mono, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, color: s === out ? CREW : '#ffd1da' }}>
                  {s === out ? t.revealCaught : t.revealAway}
                </span>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 'clamp(16px, 3.4vh, 28px)', fontFamily: mono, fontSize: 12, letterSpacing: 2, color: '#ffc2cf' }}>{t.yourPoints}</div>
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {lines.length > 0 ? (
            lines.map((line) => (
              <div key={line} style={{ fontFamily: sans, fontSize: 24, fontWeight: 800, letterSpacing: 1, color: line.startsWith('−') ? '#ff8fa8' : '#ffffff' }}>
                {line}
              </div>
            ))
          ) : (
            <div style={{ fontFamily: sans, fontSize: 19, fontWeight: 600, color: '#ffd1da' }}>{t.noPoints}</div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
